import { useMemo } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { InfoCard, Link } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/esm/useAsync';
import {
  boschBlue,
  boschGray,
} from '../../modules/theme/boschColors';

const useStyles = makeStyles(theme => ({
  list: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.25),
    borderTop: `1px solid ${boschGray[90]}`,
    '&:first-child': { borderTop: 'none' },
  },
  itemLeft: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  name: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: boschBlue[40],
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
  description: {
    fontSize: '0.8rem',
    color: boschGray[40],
    marginTop: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chip: {
    backgroundColor: boschBlue[95],
    color: boschBlue[40],
    fontWeight: 600,
    fontSize: '0.65rem',
    height: 20,
  },
  empty: {
    padding: theme.spacing(2),
    color: boschGray[40],
    fontStyle: 'italic',
    fontSize: '0.85rem',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
}));

export type EntityListProps = {
  title?: string;
  /** `Component | API | System | Resource | Group | User`. */
  kind?: string;
  /** Optional `spec.type` filter (e.g. `service`, `library`, `team`). */
  type?: string;
  /** Optional `spec.owner` ref (`group:default/<name>`). */
  owner?: string;
  /** Max entities to render. Default 10 — long pages get crowded fast. */
  limit?: number;
};

/**
 * Live list of catalog entities matching the supplied filters. Used
 * for things like "Components owned by my squad", "All Systems",
 * "APIs in production", etc.
 *
 * Why a dedicated widget instead of a markdown table:
 *   - The catalog is the source of truth, so a hand-maintained list
 *     in markdown rots fast (new components don't appear; renamed
 *     ones leave a stale link).
 *   - A widget rendered from a CATALOG QUERY guarantees the section
 *     always reflects the current state, with zero maintenance.
 *
 * Each item links to the entity's catalog detail page so the list
 * doubles as a navigation aid.
 *
 * Why we render via raw `<a>` style classes instead of an `InfoCard`
 * Table: a tiny custom list is easier to skim at 0.95rem / 6 rows.
 * If a future widget wants the full sortable Backstage Table we'll
 * add a separate `entity-table` widget alongside this one.
 */
export const EntityList = ({
  title,
  kind = 'Component',
  type,
  owner,
  limit = 10,
}: EntityListProps) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  /*
   * Memo the filter object so `useAsync` doesn't refetch on every
   * render — without this React would see a new object identity each
   * time, defeating the dependency-array short-circuit. Subtle but
   * important: a parent page with 10 widgets re-rendering hourly
   * would otherwise hammer the catalog API.
   */
  const filter = useMemo(() => {
    const f: Record<string, string> = { kind };
    if (type) f['spec.type'] = type;
    if (owner) f['spec.owner'] = owner;
    return f;
  }, [kind, type, owner]);

  const {
    value: items = [],
    loading,
    error,
  } = useAsync(async () => {
    const { items: results } = await catalogApi.getEntities({
      filter,
      fields: [
        'metadata.name',
        'metadata.namespace',
        'metadata.title',
        'metadata.description',
        'spec.type',
        'kind',
      ],
    });
    return results.slice(0, Math.max(0, limit));
  }, [catalogApi, filter, limit]);

  const headerTitle =
    title ?? `${kind}${type ? ` · ${type}` : ''}${owner ? ` · ${owner}` : ''}`;

  return (
    <InfoCard title={headerTitle}>
      {loading && (
        <Box className={classes.loading}>
          <CircularProgress size={28} />
        </Box>
      )}
      {error && (
        <Typography className={classes.empty}>
          Failed to load entities: {error.message}
        </Typography>
      )}
      {!loading && !error && items.length === 0 && (
        <Typography className={classes.empty}>
          No entities match the filter.
        </Typography>
      )}
      {!loading && !error && items.length > 0 && (
        <ul className={classes.list}>
          {items.map(entity => {
            const namespace = entity.metadata.namespace ?? 'default';
            const href = `/catalog/${namespace}/${entity.kind.toLowerCase()}/${entity.metadata.name}`;
            const displayName =
              entity.metadata.title ?? entity.metadata.name;
            return (
              <li key={`${entity.kind}-${entity.metadata.name}`} className={classes.item}>
                <div className={classes.itemLeft}>
                  <Link to={href} className={classes.name}>
                    {displayName}
                  </Link>
                  {entity.metadata.description && (
                    <span className={classes.description}>
                      {entity.metadata.description}
                    </span>
                  )}
                </div>
                {(entity.spec as { type?: string } | undefined)?.type && (
                  <Chip
                    label={(entity.spec as { type: string }).type}
                    size="small"
                    className={classes.chip}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </InfoCard>
  );
};
